/**
 * PriceDisplay Component
 * Formatted price display with various price types
 * BP-08-04: Marketplace Frontend (Family)
 */

import React from 'react';
import { DollarSign, Phone, FileText, Shield } from 'lucide-react';
import type { PriceDisplayProps } from '@/features/marketplace/types/marketplaceTypes';

const sizeClasses = {
  sm: {
    price: 'text-base font-semibold',
    label: 'text-xs',
    icon: 'w-3 h-3',
  },
  md: {
    price: 'text-xl font-bold',
    label: 'text-sm',
    icon: 'w-4 h-4',
  },
  lg: {
    price: 'text-3xl font-bold',
    label: 'text-base',
    icon: 'w-5 h-5',
  },
};

const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

export const PriceDisplay: React.FC<PriceDisplayProps> = ({
  priceMin,
  priceMax,
  priceType,
  size = 'md',
  showInsurance = false,
  acceptsInsurance = false,
}) => {
  const classes = sizeClasses[size];
  
  const renderPrice = () => {
    switch (priceType) {
      case 'fixed':
        if (priceMin !== null) {
          return (
            <span className={`${classes.price} text-navy`}>
              {formatPrice(priceMin)}
            </span>
          );
        }
        return null;
        
      case 'range':
        if (priceMin !== null && priceMax !== null) {
          return (
            <span className={`${classes.price} text-navy`}>
              {formatPrice(priceMin)} – {formatPrice(priceMax)}
            </span>
          );
        }
        if (priceMin !== null) {
          return (
            <span className={`${classes.price} text-navy`}>
              From {formatPrice(priceMin)}
            </span>
          );
        }
        return null;
        
      case 'quote':
        return (
          <div className="flex items-center gap-2">
            <FileText className={`${classes.icon} text-sage`} />
            <span className={`${classes.label} font-semibold text-sage-dark`}>
              Request Quote
            </span>
          </div>
        );
        
      case 'call':
        return (
          <div className="flex items-center gap-2">
            <Phone className={`${classes.icon} text-sage`} />
            <span className={`${classes.label} font-semibold text-sage-dark`}>
              Call for Pricing
            </span>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="flex flex-col gap-1">
      {renderPrice()}
      
      {showInsurance && acceptsInsurance && (
        <div className="flex items-center gap-1.5 text-sage-dark">
          <Shield className={classes.icon} />
          <span className={classes.label}>Insurance Accepted</span>
        </div>
      )}
    </div>
  );
};

export default PriceDisplay;

